/**
 * @file きょうの復習 (本だなの上に出す復習カードの面)。
 *
 * @what: まなぶ人の active なカードを「きょうの復習」(期日が今日以前) と「この先の復習」
 *   に分けて出す。きょうの 1 枚は、問い・出典 (資料名 p.N へのリンク)・「こたえを見る」
 *   (答えがあれば) と、できた / むずかしかった / できなかった の 3 つのボタン。押すと結果を
 *   記録し、サーバが決めた次の期日を 1 行で知らせる。この先の復習は折りたたみで、期日と
 *   問いと「やめる」(retired にする)。
 * @why: 復習の予定が期日に画面へ出て、その場で結果を残せると、スキルが書く先と web が
 *   見せる先が揃う (§11)。間隔は結果で伸び縮みし、数字 (回数・達成率) は出さない。
 * @failure: 取得失敗は 1 行で知らせる。1 枚も無ければ何も出さない (面を占めない)。
 *   操作の失敗は文言のみ (詳細は console)。
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { addDaysLocal, todayLocal } from "../api/dates";
import type { StudyReviewCard, StudyReviewResultKind } from "../api/reviewCards";
import type { StudySource } from "../api/sources";
import type { ReviewCardsState } from "../hooks/useReviewCards";
import {
  REVIEW_RESULT_LABELS,
  activeCardsOf,
  dueCards,
  dueLabel,
  intervalLabel,
  upcomingCards,
} from "../model/reviewCards";
import "./study.css";

/** 出典の表示 (資料名 p.N)。資料が消えていれば id のまま。 */
function refLabel(card: StudyReviewCard, sources: readonly StudySource[]): string | null {
  if (!card.ref) return null;
  const title = sources.find((s) => s.id === card.ref?.source_id)?.title ?? card.ref.source_id;
  return card.ref.page ? `${title} p.${card.ref.page}` : title;
}

function refHref(familyId: string, card: StudyReviewCard): string | null {
  if (!card.ref) return null;
  const base = `/study/${encodeURIComponent(familyId)}/sources/${encodeURIComponent(card.ref.source_id)}`;
  if (card.ref.item_id) return `${base}?item=${encodeURIComponent(card.ref.item_id)}`;
  if (card.ref.page) return `${base}?page=${card.ref.page}`;
  return base;
}

/** ボタンの色 (既存の study-review-btn の ok / wrong を使い、むずかしかった は hard)。 */
const RESULT_CLASS: Record<StudyReviewResultKind, string> = { ok: "ok", hard: "hard", ng: "wrong" };

export function ReviewCardsPanel({
  familyId,
  learnerPersonId,
  sources,
  state,
  onAnswer,
  onRetire,
  busy,
  actionError,
}: {
  familyId: string;
  learnerPersonId: string | null;
  sources: readonly StudySource[];
  state: ReviewCardsState;
  onAnswer: (cardId: string, result: StudyReviewResultKind) => Promise<boolean>;
  onRetire: (card: StudyReviewCard) => Promise<void>;
  busy: boolean;
  actionError: string | null;
}) {
  const today = todayLocal();
  const tomorrow = addDaysLocal(today, 1);
  const [showAnswer, setShowAnswer] = useState<Set<string>>(new Set());
  const [upcomingOpen, setUpcomingOpen] = useState(false);
  // 直前に答えたカードの id (サーバが決めた次の期日を 1 行で知らせる)。
  const [justAnsweredId, setJustAnsweredId] = useState<string | null>(null);

  const active = useMemo(
    () =>
      state.status === "ready" && learnerPersonId
        ? activeCardsOf(state.cards, learnerPersonId)
        : [],
    [state, learnerPersonId],
  );
  const due = useMemo(() => dueCards(active, today), [active, today]);
  const upcoming = useMemo(() => upcomingCards(active, today), [active, today]);

  if (state.status === "error") {
    return (
      <p role="alert" className="study-error">
        復習カードを読み込めませんでした。時間を置いて再読み込みしてください。
      </p>
    );
  }
  if (state.status !== "ready" || !learnerPersonId || active.length === 0) return null;

  const answer = async (card: StudyReviewCard, result: StudyReviewResultKind) => {
    const ok = await onAnswer(card.id, result);
    if (ok) setJustAnsweredId(card.id);
  };

  // 答えた直後のカード (一覧は hook が差し替えるので、そこからサーバが決めた次の期日を引く)。
  const answered = justAnsweredId ? state.cards.find((c) => c.id === justAnsweredId) : undefined;

  return (
    <section className="study-review" aria-label="復習カード">
      <div className="study-review-head">
        <h3 className="study-review-title">
          きょうの復習 <span className="study-review-count">{due.length} 件</span>
        </h3>
        {actionError ? (
          <span role="alert" className="study-save-error">
            {actionError}
          </span>
        ) : null}
      </div>
      {answered ? (
        <p className="study-review-just" role="status">
          「{answered.prompt}」は {intervalLabel(answered.interval_days)}（
          {dueLabel(answered.due_on, today, tomorrow)}）にもう一度出ます。
        </p>
      ) : null}
      {due.length === 0 ? (
        <p className="study-muted">きょうの復習はありません。</p>
      ) : (
        <ul className="study-review-list">
          {due.map((card) => {
            const label = refLabel(card, sources);
            const href = refHref(familyId, card);
            const open = showAnswer.has(card.id);
            return (
              <li key={card.id} className="study-review-card">
                {card.topic ? <div className="study-review-topic">{card.topic}</div> : null}
                <div className="study-review-prompt">{card.prompt}</div>
                <div className="study-review-meta">
                  {label && href ? <Link to={href}>{label}</Link> : label}
                  {card.due_on < today ? (
                    <span className="study-review-overdue">
                      {dueLabel(card.due_on, today, tomorrow)}
                    </span>
                  ) : null}
                </div>
                {card.answer ? (
                  open ? (
                    <div className="study-review-answer">{card.answer}</div>
                  ) : (
                    <button
                      type="button"
                      className="study-link-btn"
                      onClick={() => setShowAnswer((prev) => new Set(prev).add(card.id))}
                    >
                      こたえを見る
                    </button>
                  )
                ) : null}
                <div
                  className="study-review-actions"
                  role="group"
                  aria-label={`${card.prompt} の結果`}
                >
                  {(["ok", "hard", "ng"] as const).map((r) => (
                    <button
                      key={r}
                      type="button"
                      className={`study-review-btn study-review-btn--${RESULT_CLASS[r]}`}
                      disabled={busy}
                      onClick={() => void answer(card, r)}
                    >
                      {REVIEW_RESULT_LABELS[r]}
                    </button>
                  ))}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {upcoming.length > 0 ? (
        <div className="study-review-upcoming">
          <button
            type="button"
            className="study-link-btn"
            aria-expanded={upcomingOpen}
            onClick={() => setUpcomingOpen((v) => !v)}
          >
            この先の復習 {upcoming.length} 件{upcomingOpen ? " をたたむ" : " を見る"}
          </button>
          {upcomingOpen ? (
            <ul className="study-review-upcoming-list">
              {upcoming.map((card) => (
                <li key={card.id}>
                  <span className="study-review-upcoming-due">
                    {dueLabel(card.due_on, today, tomorrow)}
                  </span>
                  <span className="study-review-upcoming-prompt">{card.prompt}</span>
                  <button
                    type="button"
                    className="study-link-btn"
                    disabled={busy}
                    onClick={() => void onRetire(card)}
                    aria-label={`${card.prompt} の復習をやめる`}
                  >
                    やめる
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
